import { createIcons, Boxes, Activity, ShieldCheck, Cpu, ArrowRight, Layers, CheckCircle, AlertTriangle, RefreshCw, FileText, Wrench } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";

class AppTechnicalAssetsHubPage extends HTMLElement {
  private stats = {
    equipmentsCount: 0,
    instrumentsCount: 0,
    safetyDevicesCount: 0,
    instrumentsDueCount: 0,
    safetyDevicesDueCount: 0,
    activeClientName: "Todos os Clientes"
  };
  private selectedClientId: string = "";
  private clients: Array<{ id: string; name: string }> = [];
  private isLoading = true;

  connectedCallback() {
    this.initClientsAndLoad();
  }

  private async initClientsAndLoad() {
    try {
      if (window.app?.platform?.adminService) {
        const cls = await window.app.platform.adminService.listAuthorizedClients({} as any).catch(() => []);
        this.clients = (cls || []).map((c: any) => ({ id: c.id, name: c.name || c.legalName }));
      }
    } catch {
      /* ignore client loading errors */
    }
    await this.loadStats();
  }

  private async loadStats() {
    this.isLoading = true;
    this.render();

    try {
      const dummyContext = { activeClientId: this.selectedClientId || undefined, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;

      const [equipments, instruments, safetyDevices] = await Promise.all([
        window.app.platform.equipmentService.listEquipments(dummyContext).catch(() => []),
        window.app.platform.instrumentService.listInstruments(dummyContext).catch(() => []),
        window.app.platform.safetyDeviceService.listSafetyDevices(dummyContext).catch(() => [])
      ]);

      const now = new Date();
      const instrumentsDue = instruments.filter((i: any) => !i.nextCalibrationDate || new Date(i.nextCalibrationDate) < now || i.status === 'CALIBRATION_DUE').length;
      const safetyDevicesDue = safetyDevices.filter((s: any) => !s.nextTestDate || new Date(s.nextTestDate) < now || s.status === 'TEST_DUE').length;

      const clientName = this.selectedClientId 
        ? (this.clients.find(c => c.id === this.selectedClientId)?.name || "Cliente Selecionado")
        : "Todos os Clientes Autorizados";

      this.stats = {
        equipmentsCount: equipments.length,
        instrumentsCount: instruments.length,
        safetyDevicesCount: safetyDevices.length,
        instrumentsDueCount: instrumentsDue,
        safetyDevicesDueCount: safetyDevicesDue,
        activeClientName: clientName
      };

      this.isLoading = false;
      this.render();
    } catch (e) {
      this.isLoading = false;
      this.render();
    }
  }

  private render() {
    this.innerHTML = `
      <style>
        .hub-container {
          padding: 24px;
          max-width: 1200px;
          margin: 0 auto;
          color: var(--color-text-primary);
        }
        .hub-header {
          margin-bottom: 24px;
        }
        .hub-header h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 6px 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .hub-header p {
          margin: 0;
          color: var(--color-text-primary);
          font-size: 0.9rem;
        }
        .hub-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }
        .asset-card {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          transition: transform 0.2s, border-color 0.2s;
          cursor: pointer;
        }
        .asset-card:hover {
          border-color: var(--color-ink);
          transform: translateY(-2px);
        }
        .card-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .card-icon-box {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .card-title {
          font-size: 1.15rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 6px 0;
        }
        .card-desc {
          font-size: 0.85rem;
          color: var(--color-text-disabled);
          line-height: 1.4;
          margin-bottom: 16px;
        }
        .card-metrics {
          display: flex;
          gap: 16px;
          padding: 12px 0;
          border-top: 1px solid #27272a;
          border-bottom: 1px solid #27272a;
          margin-bottom: 16px;
        }
        .metric-item {
          display: flex;
          flex-direction: column;
        }
        .metric-val {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .metric-lbl {
          font-size: 0.72rem;
          color: var(--color-text-primary);
          text-transform: uppercase;
        }
        .card-footer-btn {
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: var(--ia-info-fg, #60a5fa);
          font-size: 0.875rem;
          font-weight: 500;
        }
      </style>

      <div class="hub-container">
        <div class="hub-header" style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px;">
          <div>
            <h1><i data-lucide="layers" style="color: var(--color-ink);"></i> Hub de Ativos Técnicos</h1>
            <p>Gestão centralizada de Equipamento / Tubulação, Instrumentação de Medição e Dispositivos de Alívio (PSV) para <strong>${this.stats.activeClientName}</strong>.</p>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 0.85rem; color: var(--color-text-disabled); font-weight: 500;">Filtrar por Cliente:</span>
            <select id="hub-client-filter" style="padding: 6px 12px; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); color: var(--color-text-primary); border-radius: 6px; font-size: 0.85rem;">
              <option value="">Todos os Clientes</option>
              ${this.clients.map(c => `<option value="${c.id}" ${this.selectedClientId === c.id ? 'selected' : ''}>${c.name}</option>`).join('')}
            </select>
          </div>
        </div>

        <div class="hub-grid">
          <!-- EQUIPAMENTOS -->
          <div class="asset-card" id="card-equipment">
            <div>
              <div class="card-top">
                <div class="card-icon-box" style="background: rgba(59, 130, 246, 0.15); color: var(--ia-info-fg, #60a5fa);">
                  <i data-lucide="boxes" style="width: 24px; height: 24px;"></i>
                </div>
              </div>
              <h2 class="card-title">Equipamento / Tubulação</h2>
              <div class="card-desc">
                Vasos de pressão (VP), caldeiras (CD), tanques de armazenamento (TQ), permutadores, sistemas de tubulação (ST) e tubulações industriais sob a NR-13.
              </div>
            </div>
            <div>
              <div class="card-metrics">
                <div class="metric-item">
                  <span class="metric-val">${this.stats.equipmentsCount}</span>
                  <span class="metric-lbl">Equipamentos & Tubulações</span>
                </div>
              </div>
              <div class="card-footer-btn">
                <span>Acessar Equipamento / Tubulação</span>
                <i data-lucide="arrow-right" style="width: 16px; height: 16px;"></i>
              </div>
            </div>
          </div>

          <!-- INSTRUMENTOS -->
          <div class="asset-card" id="card-instruments">
            <div>
              <div class="card-top">
                <div class="card-icon-box" style="background: rgba(168, 85, 247, 0.15); color: var(--ia-info-fg, #c084fc);">
                  <i data-lucide="cpu" style="width: 24px; height: 24px;"></i>
                </div>
              </div>
              <h2 class="card-title">Instrumentação & Calibração</h2>
              <div class="card-desc">
                Manômetros, termômetros, transmissores, pressostatos e medidores de nível com controle de histórico de calibração RBC.
              </div>
            </div>
            <div>
              <div class="card-metrics">
                <div class="metric-item">
                  <span class="metric-val">${this.stats.instrumentsCount}</span>
                  <span class="metric-lbl">Instrumentos</span>
                </div>
                <div class="metric-item">
                  <span class="metric-val" style="color: ${this.stats.instrumentsDueCount > 0 ? 'var(--status-danger-solid)' : 'var(--ia-success-fg, #4ade80)'};">${this.stats.instrumentsDueCount}</span>
                  <span class="metric-lbl">Calibrações Pendentes</span>
                </div>
              </div>
              <div class="card-footer-btn">
                <span>Acessar Instrumentos</span>
                <i data-lucide="arrow-right" style="width: 16px; height: 16px;"></i>
              </div>
            </div>
          </div>

          <!-- DISPOSITIVOS DE SEGURANÇA -->
          <div class="asset-card" id="card-safety-devices">
            <div>
              <div class="card-top">
                <div class="card-icon-box" style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid);">
                  <i data-lucide="shield-check" style="width: 24px; height: 24px;"></i>
                </div>
              </div>
              <h2 class="card-title">Dispositivos de Segurança (PSV)</h2>
              <div class="card-desc">
                Válvulas de segurança e alívio de pressão, discos de ruptura e quebra-vácuo com controle de bancada e laudos de estanqueidade API 527.
              </div>
            </div>
            <div>
              <div class="card-metrics">
                <div class="metric-item">
                  <span class="metric-val">${this.stats.safetyDevicesCount}</span>
                  <span class="metric-lbl">Dispositivos</span>
                </div>
                <div class="metric-item">
                  <span class="metric-val" style="color: ${this.stats.safetyDevicesDueCount > 0 ? 'var(--status-danger-solid)' : 'var(--ia-success-fg, #4ade80)'};">${this.stats.safetyDevicesDueCount}</span>
                  <span class="metric-lbl">Testes Pendentes</span>
                </div>
              </div>
              <div class="card-footer-btn">
                <span>Acessar Dispositivos de Segurança</span>
                <i data-lucide="arrow-right" style="width: 16px; height: 16px;"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { Boxes, Activity, ShieldCheck, Cpu, ArrowRight, Layers, CheckCircle, AlertTriangle, RefreshCw, FileText, Wrench },
      nameAttr: "data-lucide",
      root: this
    });

    this.querySelector('#hub-client-filter')?.addEventListener('change', async (e) => {
      this.selectedClientId = (e.target as HTMLSelectElement).value;
      await this.loadStats();
    });

    this.querySelector('#card-equipment')?.addEventListener('click', () => {
      router.navigate('/equipment');
    });

    this.querySelector('#card-instruments')?.addEventListener('click', () => {
      router.navigate('/instruments');
    });

    this.querySelector('#card-safety-devices')?.addEventListener('click', () => {
      router.navigate('/safety-devices');
    });
  }
}

if (!customElements.get("app-technical-assets-hub-page")) {
  customElements.define("app-technical-assets-hub-page", AppTechnicalAssetsHubPage);
}



